import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import Tag from "@/components/base/Tag";
import { useSeo } from "@/hooks/useSeo";
import AboutFaq, { faqItems } from "@/pages/about/components/AboutFaq";

const positions = [
  {
    icon: "ri-stack-line",
    title: "话题聚合",
    text: "把同一事件的时间线、当事方回应与后续进展收在一个页面，避免被单条切片带偏判断。",
  },
  {
    icon: "ri-chat-quote-line",
    title: "评论复盘",
    text: "把评论区高能回复与网友神评论单独拎出来，让有价值的补充信息不被刷屏淹没。",
  },
  {
    icon: "ri-bar-chart-2-line",
    title: "风向呈现",
    text: "用投票结果与态度分布还原讨论现场，支持、中立、反对三段并列，不做单一结论输出。",
  },
];

const principles = [
  {
    title: "观点并列，不代站队",
    text: "每个话题同时呈现支持与反对的理由，编辑只负责整理结构与核对时间线。",
  },
  {
    title: "区分事实与情绪",
    text: "可核实的信息写进事件梳理，主观判断集中在评论与投票板块，两者不混在同一段里。",
  },
  {
    title: "有争议就标注",
    text: "当事方已经回应、但材料仍有出入的部分，我们会在段落里明确写清尚未确认。",
  },
  {
    title: "数据说话",
    text: "态度分布只呈现参与投票者的比例，并同步标注参与人数，方便判断样本可信度。",
  },
];

const milestones = [
  {
    year: "2024",
    title: "从评论区整理开始",
    text: "最初只是把几个热门话题下被反复引用回复抄录成文档，方便自己回头看风向。",
  },
  {
    year: "2025",
    title: "加入投票与态度分布",
    text: "发现只看评论容易误判，于是把投票结果和态度占比一起放进话题页，形成现在的结构。",
  },
  {
    year: "2026",
    title: "形成六个常驻栏目",
    text: "栏目从娱乐扩展到影视、网络、社会、游戏与消费，话题线索开始由网友投递补充。",
  },
];

const testimonials = [
  {
    nickname: "蹲评论区的人",
    text: "以前刷热搜要翻好几百条评论才能看出大家在争什么，现在看态度分布那三条就够了，省时间。",
    topic: "来自话题：加班文化再上热搜",
  },
  {
    nickname: "不吃瓜的编辑",
    text: "观点并列这一段做得挺克制，支持与反对的理由分开放，至少不会看着看着就被带着走。",
    topic: "来自话题：综艺剪辑被指刻意制造冲突",
  },
  {
    nickname: "数据敏感星人",
    text: "每个投票都写了参与人数，这一点很关键。人少的投票就算比例夸张，我也知道不能当全貌。",
    topic: "来自话题：抽卡概率公示后玩家仍不买账",
  },
];

export default function About() {
  useSeo({
    title: "关于我们 - 品牌介绍、内容原则与联系方式 | 爆料看点",
    description:
      "了解爆料看点的网站定位、发展理念与内容原则。我们整理话题、评论区高能回复、网友神评论与投票结果展示，并同步给出舆论态度分布，欢迎投递线索。",
    keywords:
      "关于爆料看点,内容原则,舆论态度分布,评论区高能回复,网友神评论,投稿线索",
    path: "/about",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于我们 - 爆料看点",
        url: "https://qrwho.cn/about",
        inLanguage: "zh-CN",
        description:
          "介绍爆料看点的网站定位、发展理念、内容原则与联系方式。",
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqItems.map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: item.answer,
          },
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "首页",
            item: "https://qrwho.cn/",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "关于我们",
            item: "https://qrwho.cn/about",
          },
        ],
      },
    ],
  });

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full px-4 py-8 md:px-8 md:py-12">
          <Breadcrumbs items={[{ label: "关于我们" }]} />
          <span className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-3 py-1 font-label text-xs text-primary-700">
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-information-line" aria-hidden="true" />
            </span>
            品牌介绍 · 内容原则 · 联系方式
          </span>
          <h1 className="mt-4 max-w-3xl font-heading text-2xl font-bold leading-tight text-foreground-950 md:text-4xl">
            关于爆料看点
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-700 md:text-base">
            我们是一个专注整理争议话题的编辑小组。目标很单纯：把事件本身、评论区高能回复、网友神评论、
            投票结果展示与舆论态度分布放在同一个页面里，让读者花最少的时间看清讨论现场。
          </p>
        </div>
      </div>

      <div className="mx-auto w-full px-4 py-12 md:px-8 md:py-16">
        <section className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2 lg:gap-12">
          <div>
            <div className="mb-3 flex items-center gap-2">
              <span className="h-3 w-3 rounded-sm bg-secondary-500" aria-hidden="true" />
              <span className="font-label text-xs tracking-[0.2em] text-secondary-700 uppercase">
                品牌介绍
              </span>
            </div>
            <h2 className="font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
              一群愿意把评论读完的人
            </h2>
            <div className="mt-5 space-y-4 text-sm leading-loose text-foreground-700 md:text-base">
              <p>
                爆料看点最初只是几个人共用的一份在线文档。每次遇到争议事件，我们会把评论区里被反复引用的回复
                挑出来抄进文档，顺手记下当事方有没有回应。时间久了，这份文档变成了现在的话题页。
              </p>
              <p>
                和传统资讯不同，我们不做结论式报道。事件梳理只写可核实的时间与回应，观点交锋把支持与反对
                的理由并列摆放，投票与态度分布则交给读者自己判断。
              </p>
              <p>
                目前网站设有娱乐八卦、影视综艺、网络热点、社会民生、游戏电竞与生活消费六个常驻栏目，
                所有话题都可以通过邮件投递线索，也欢迎指出事实错误。
              </p>
            </div>
            <div className="mt-6 flex flex-wrap gap-2">
              <Tag label="观点并列" tone="primary" />
              <Tag label="态度分布" tone="accent" />
              <Tag label="评论精选" tone="secondary" />
              <Tag label="线索投递" tone="neutral" />
            </div>
          </div>
          <div className="h-[280px] w-full overflow-hidden rounded-lg bg-background-200 md:h-[420px]">
            <img
              src="https://readdy.ai/api/search-image?query=Editorial%20illustration%20poster%20style%20with%20warm%20cream%20paper%20background%20and%20soft%20studio%20lighting%2C%20stylized%20abstract%20composition%20representing%20an%20editorial%20desk%20with%20newspapers%20a%20magnifier%20and%20speech%20bubbles%2C%20muted%20crimson%20teal%20and%20amber%20accents%2C%20gentle%20grain%20texture%2C%20high%20detail%2C%20centered%20subject%2C%20no%20text&width=1000&height=800&seq=bl-about-intro&orientation=landscape"
              alt="爆料看点编辑整理话题与评论的工作场景插画"
              title="关于爆料看点 - 话题整理与评论精选"
              className="h-full w-full object-cover object-top"
            />
          </div>
        </section>

        <section className="mt-16">
          <div className="mb-3 flex items-center gap-2">
            <span className="h-3 w-3 rounded-sm bg-primary-500" aria-hidden="true" />
            <span className="font-label text-xs tracking-[0.2em] text-primary-700 uppercase">
              网站定位
            </span>
          </div>
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            我们只做三件事
          </h2>
          <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-3">
            {positions.map((item) => (
              <article key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                  <i className={`${item.icon} text-xl`} aria-hidden="true" />
                </span>
                <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="mt-16 grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-12">
          <div>
            <div className="mb-3 flex items-center gap-2">
              <span className="h-3 w-3 rounded-sm bg-accent-500" aria-hidden="true" />
              <span className="font-label text-xs tracking-[0.2em] text-accent-700 uppercase">
                发展理念
              </span>
            </div>
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              从一份文档到六个栏目
            </h2>
            <ol className="mt-6 space-y-6 border-l border-background-300 pl-6">
              {milestones.map((item) => (
                <li key={item.year} className="relative">
                  <span className="absolute -left-[31px] top-1.5 h-3 w-3 rounded-full border-2 border-background-50 bg-accent-500" />
                  <span className="font-label text-xs tracking-[0.16em] text-accent-700">
                    {item.year}
                  </span>
                  <h3 className="mt-1 font-heading text-base font-bold text-foreground-950">
                    {item.title}
                  </h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{item.text}</p>
                </li>
              ))}
            </ol>
          </div>

          <div>
            <div className="mb-3 flex items-center gap-2">
              <span className="h-3 w-3 rounded-sm bg-secondary-500" aria-hidden="true" />
              <span className="font-label text-xs tracking-[0.2em] text-secondary-700 uppercase">
                内容原则
              </span>
            </div>
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              四条不能绕开的底线
            </h2>
            <div className="mt-6 space-y-4">
              {principles.map((item, index) => (
                <article key={item.title} className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-secondary-50 font-heading text-sm font-bold text-secondary-700">
                    0{index + 1}
                  </span>
                  <div>
                    <h3 className="font-heading text-base font-bold text-foreground-950">
                      {item.title}
                    </h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{item.text}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="mt-16">
          <div className="mb-3 flex items-center gap-2">
            <span className="h-3 w-3 rounded-sm bg-primary-500" aria-hidden="true" />
            <span className="font-label text-xs tracking-[0.2em] text-primary-700 uppercase">
              用户口碑
            </span>
          </div>
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            读者说这几件事最有用
          </h2>
          <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-3">
            {testimonials.map((item) => (
              <figure key={item.nickname} className="flex h-full flex-col rounded-lg border border-background-200 bg-background-100 p-6">
                <span className="flex h-8 w-8 items-center justify-center text-accent-500">
                  <i className="ri-double-quotes-l text-2xl" aria-hidden="true" />
                </span>
                <blockquote className="mt-3 flex-1 text-sm leading-loose text-foreground-700">
                  {item.text}
                </blockquote>
                <figcaption className="mt-4 border-t border-background-300 pt-3">
                  <span className="block font-label text-sm font-medium text-foreground-900">
                    {item.nickname}
                  </span>
                  <span className="mt-0.5 block font-label text-xs text-foreground-500">
                    {item.topic}
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        <div className="mt-16">
          <AboutFaq />
        </div>

        <section className="mt-16 rounded-lg border border-background-300 bg-background-100 p-6 md:p-10">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            <div>
              <div className="mb-3 flex items-center gap-2">
                <span className="h-3 w-3 rounded-sm bg-secondary-500" aria-hidden="true" />
                <span className="font-label text-xs tracking-[0.2em] text-secondary-700 uppercase">
                  联系方式
                </span>
              </div>
              <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                线索、更正与合作咨询
              </h2>
              <p className="mt-4 text-sm leading-loose text-foreground-700">
                无论是想补充话题后续、指出事实错误，还是对内容原则有疑问，都可以直接发邮件。
                线索类邮件请尽量附上时间、地点与可核实的材料，我们会优先处理。
              </p>
            </div>
            <div className="space-y-3">
              <a
                href="mailto:baoliao@example.com"
                className="flex cursor-pointer items-center gap-3 rounded-lg border border-background-300 bg-background-50 px-5 py-4 transition-colors hover:border-primary-300 hover:bg-primary-50"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className="ri-mail-line text-lg" aria-hidden="true" />
                </span>
                <span className="min-w-0">
                  <span className="block font-label text-xs text-foreground-500">内容与线索</span>
                  <span className="block font-heading text-base font-bold text-foreground-950">
                    baoliao@example.com
                  </span>
                </span>
                <span className="ml-auto flex h-5 w-5 items-center justify-center text-foreground-400">
                  <i className="ri-arrow-right-up-line" aria-hidden="true" />
                </span>
              </a>
              <div className="flex items-center gap-3 rounded-lg border border-background-300 bg-background-50 px-5 py-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                  <i className="ri-time-line text-lg" aria-hidden="true" />
                </span>
                <span className="min-w-0">
                  <span className="block font-label text-xs text-foreground-500">回复时效</span>
                  <span className="block font-heading text-base font-bold text-foreground-950">
                    工作日 24 小时内
                  </span>
                </span>
              </div>
              <Link
                to="/topics"
                className="flex cursor-pointer items-center gap-3 rounded-lg bg-primary-500 px-5 py-4 transition-colors hover:bg-primary-600"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-background-50/20 text-background-50">
                  <i className="ri-fire-line text-lg" aria-hidden="true" />
                </span>
                <span className="min-w-0">
                  <span className="block font-label text-xs text-background-200">先去逛逛</span>
                  <span className="block font-heading text-base font-bold text-background-50">
                    看今日热榜话题
                  </span>
                </span>
                <span className="ml-auto flex h-5 w-5 items-center justify-center text-background-100">
                  <i className="ri-arrow-right-line" aria-hidden="true" />
                </span>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}